import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider } from '@/components/ThemeProvider';
import { AuthProvider } from '@/components/AuthProvider';
import { RequireAuth } from '@/components/RequireAuth';
import { AppShell } from '@/components/AppShell';
import { Toaster } from '@/components/ui/toaster';
import { CommandBar } from '@/components/CommandBar';
import { SupportWidget } from '@/components/SupportWidget';
import '@/App.css';

import LoginPage from '@/pages/LoginPage';
import SignupPage from '@/pages/SignupPage';
import PortalPage from '@/pages/PortalPage';
import { useAuth } from '@/components/AuthProvider';

import DashboardPage from '@/pages/DashboardPage';
import LoadsPage from '@/pages/LoadsPage';
import DispatchPage from '@/pages/DispatchPage';
import TripsPage from '@/pages/TripsPage';
import TrackingPage from '@/pages/TrackingPage';
import PartiesPage from '@/pages/PartiesPage';
import FleetPage from '@/pages/FleetPage';
import DriversPage from '@/pages/DriversPage';

import SettlementsPage from '@/pages/SettlementsPage';
import PayRunsPage from '@/pages/PayRunsPage';
import InvoicesPage from '@/pages/InvoicesPage';
import ARPage from '@/pages/ARPage';
import CollectionsPage from '@/pages/CollectionsPage';
import FactoringPage from '@/pages/FactoringPage';
import APPage from '@/pages/APPage';
import IFTAPage from '@/pages/IFTAPage';
import CalculatorPage from '@/pages/CalculatorPage';

import ImportPage from '@/pages/ImportPage';
import AIPage from '@/pages/AIPage';
import QuickBooksPage from '@/pages/QuickBooksPage';
import DocumentsPage from '@/pages/DocumentsPage';
import GuidesPage from '@/pages/GuidesPage';
import SettingsPage from '@/pages/SettingsPage';
import SuperOverviewPage from '@/pages/super/SuperOverviewPage';
import SuperCompaniesPage from '@/pages/super/SuperCompaniesPage';
import SuperBillingPage from '@/pages/super/SuperBillingPage';
import SuperAuditPage from '@/pages/super/SuperAuditPage';
import SuperUsersPage from '@/pages/super/SuperUsersPage';
import SuperErrorsPage from '@/pages/super/SuperErrorsPage';
import SuperMetricsPage from '@/pages/super/SuperMetricsPage';
import SuperSecurityPage from '@/pages/super/SuperSecurityPage';
import SuperMarketingPage from '@/pages/super/SuperMarketingPage';
import SuperAppsPage from '@/pages/super/SuperAppsPage';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      gcTime: 10 * 60_000,
      retry: 1,
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
    },
    mutations: { retry: 0 },
  },
});

function GlobalWidgets() {
  const location = useLocation();
  const { user } = useAuth();
  const bare =
    ['/login', '/signup'].includes(location.pathname) || location.pathname.startsWith('/portal');
  const verifying = Boolean(user && user.role !== 'super_admin' && user.emailVerified === false);
  if (bare || verifying) return null;
  return (
    <>
      <CommandBar />
      <SupportWidget />
    </>
  );
}

/** Shell only after auth + email verification (RequireAuth may render VerifyEmailPage). */
function AuthenticatedLayout() {
  return (
    <RequireAuth>
      <AppShell>
        <Outlet />
      </AppShell>
    </RequireAuth>
  );
}

function HomeRoute() {
  const { user } = useAuth();
  // Driver portal / My loads hidden until the drivers app is live
  if (user?.role === 'driver') {
    return (
      <div className="max-w-lg space-y-2 py-10">
        <h1 className="text-xl font-semibold">Drivers app coming soon</h1>
        <p className="text-sm text-muted-foreground">
          My loads and the driver portal are temporarily unavailable. Check back once the drivers
          app launches.
        </p>
      </div>
    );
  }
  return <DashboardPage />;
}

function NotFoundRedirect() {
  const { user, ready } = useAuth();
  if (!ready) return null;
  return <Navigate to={user ? '/' : '/login'} replace />;
}

function AppRoutes() {
  return (
    <>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/portal/:token" element={<PortalPage />} />
        <Route path="/shippers" element={<Navigate to="/parties" replace />} />

        <Route element={<AuthenticatedLayout />}>
          <Route path="/" element={<HomeRoute />} />
          <Route path="/my-loads" element={<Navigate to="/" replace />} />
          <Route path="/loads" element={<LoadsPage />} />
          <Route path="/dispatch" element={<DispatchPage />} />
          <Route path="/trips" element={<TripsPage />} />
          <Route path="/tracking" element={<TrackingPage />} />
          <Route path="/parties" element={<PartiesPage />} />
          <Route path="/fleet" element={<FleetPage />} />
          <Route path="/drivers" element={<DriversPage />} />

          <Route path="/settlements" element={<SettlementsPage />} />
          <Route path="/pay-runs" element={<PayRunsPage />} />
          <Route path="/invoices" element={<InvoicesPage />} />
          <Route path="/ar" element={<ARPage />} />
          <Route path="/collections" element={<CollectionsPage />} />
          <Route path="/factoring" element={<FactoringPage />} />
          <Route path="/ap" element={<APPage />} />
          <Route path="/ifta" element={<IFTAPage />} />
          <Route path="/calculator" element={<CalculatorPage />} />

          <Route path="/import" element={<ImportPage />} />
          <Route path="/ai" element={<AIPage />} />
          <Route path="/qb" element={<QuickBooksPage />} />
          <Route path="/docs" element={<DocumentsPage />} />
          <Route path="/guides" element={<GuidesPage />} />
          <Route path="/settings" element={<SettingsPage />} />

          <Route path="/super" element={<SuperOverviewPage />} />
          <Route path="/super/companies" element={<SuperCompaniesPage />} />
          <Route path="/super/billing" element={<SuperBillingPage />} />
          <Route path="/super/marketing" element={<SuperMarketingPage />} />
          <Route path="/super/apps" element={<SuperAppsPage />} />
          <Route path="/super/audit" element={<SuperAuditPage />} />
          <Route path="/super/users" element={<SuperUsersPage />} />
          <Route path="/super/errors" element={<SuperErrorsPage />} />
          <Route path="/super/metrics" element={<SuperMetricsPage />} />
          <Route path="/super/security" element={<SuperSecurityPage />} />
        </Route>

        <Route path="*" element={<NotFoundRedirect />} />
      </Routes>
      <GlobalWidgets />
    </>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <div className="App min-h-screen bg-background text-foreground">
          <BrowserRouter>
            <AuthProvider>
              <AppRoutes />
            </AuthProvider>
          </BrowserRouter>
          <Toaster />
        </div>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
